import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { SOURCE_GROUP_ORDER, type SourceDef, type SourceGroup } from '@/features/intelligence/dashboard/catalogTypes';
import { useDashboard } from './DashboardContext';

type PickableKind = SourceDef['kind'];

interface SourcePickerProps {
  kind: PickableKind;
  value: string | null;
  onChange: (id: string) => void;
  placeholder?: string;
  /** Ids to hide from the list (e.g. metrics already charted). */
  exclude?: readonly string[];
  /** Tighter trigger for inline rows. */
  compact?: boolean;
  id?: string;
}

const KIND_NOUN: Record<PickableKind, string> = {
  metric: 'metric',
  breakdown: 'breakdown',
  table: 'table',
  forecast: 'series',
};

function unitTag(def: SourceDef): string | null {
  if (!('unit' in def)) return null;
  switch (def.unit) {
    case 'currency':
      return '$';
    case 'percent':
      return '%';
    case 'count':
      return '#';
    case 'days':
      return 'days';
    case 'months':
      return 'months';
    case 'seconds':
      return 'time';
    case 'multiple':
      return '×';
    case 'ratio':
      return 'ratio';
    case 'score':
      return 'score';
    default:
      return null;
  }
}

function groupSources(sources: SourceDef[]): Array<{ group: SourceGroup; items: SourceDef[] }> {
  return SOURCE_GROUP_ORDER.map((group) => ({ group, items: sources.filter((s) => s.group === group) })).filter(
    (g) => g.items.length > 0,
  );
}

function matches(def: SourceDef, needle: string): boolean {
  if (!needle) return true;
  const hay = `${def.label} ${def.group} ${def.description}`.toLowerCase();
  return needle
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term) => hay.includes(term));
}

export function SourcePicker({ kind, value, onChange, placeholder, exclude, compact, id }: SourcePickerProps) {
  const { catalog, isMobile } = useDashboard();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const options = useMemo(() => {
    const excluded = new Set(exclude ?? []);
    return catalog.sources.filter((s) => s.kind === kind && (!excluded.has(s.id) || s.id === value));
  }, [catalog, kind, exclude, value]);

  const selected = value ? catalog.byId.get(value) : undefined;
  const missing = !!value && !selected;
  const label = selected?.label ?? (missing ? 'Unavailable source' : (placeholder ?? `Choose a ${KIND_NOUN[kind]}`));

  if (isMobile) {
    return (
      <select
        id={id}
        value={selected ? value! : ''}
        onChange={(e) => e.target.value && onChange(e.target.value)}
        className={cn(
          'flex w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          compact ? 'h-9' : 'h-10',
          !selected && 'text-muted-foreground',
        )}
      >
        <option value="" disabled>
          {label}
        </option>
        {groupSources(options).map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.items.map((s) => (
              <option key={s.id} value={s.id}>
                {s.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    );
  }

  const filtered = options.filter((s) => matches(s, query));
  const groups = groupSources(filtered);

  const choose = (sourceId: string) => {
    onChange(sourceId);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn(
            'flex w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-left text-sm transition-colors hover:bg-secondary/60',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            compact ? 'h-9' : 'h-10',
            !selected && !missing && 'text-muted-foreground',
            missing && 'border-fresh-salmon/70 text-fresh-salmon-dark',
          )}
        >
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {selected && !compact && (
            <span className="shrink-0 rounded-full bg-secondary px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {selected.group}
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] min-w-[340px] p-0">
        <div className="flex items-center gap-2 border-b border-border px-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && filtered.length > 0) {
                e.preventDefault();
                choose(filtered[0].id);
              }
            }}
            placeholder={`Search ${options.length} ${KIND_NOUN[kind]}${options.length === 1 ? '' : 's'}…`}
            className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <div role="listbox" className="max-h-[min(60vh,420px)] overflow-y-auto p-1">
          {groups.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matches.</p>}
          {groups.map((g) => (
            <div key={g.group} className="pb-1">
              <p className="px-2 pb-1 pt-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{g.group}</p>
              {g.items.map((s) => {
                const active = s.id === value;
                const tag = unitTag(s);
                return (
                  <button
                    key={s.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => choose(s.id)}
                    className={cn(
                      'flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left hover:bg-secondary focus-visible:bg-secondary focus-visible:outline-none',
                      active && 'bg-secondary/70',
                    )}
                  >
                    <Check className={cn('mt-0.5 h-4 w-4 shrink-0', active ? 'opacity-100' : 'opacity-0')} />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate text-sm text-foreground">{s.label}</span>
                        {tag && <span className="shrink-0 text-[10px] text-muted-foreground">{tag}</span>}
                      </span>
                      <span className="line-clamp-1 text-xs text-muted-foreground">{s.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
